import { useState } from 'react'
import { toast } from 'sonner'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { apiErrorDetail } from '@/lib/apiError'
import { useCurrentUser } from '@/models/user'
import { useComplianceScope, useConfirmComplianceScope } from '@/models/vault'
import { type BusinessDraft, STEP_BUSINESS_QUESTION, StepBusiness } from './StepBusiness'
import { type ContactsDraft, STEP_CONTACTS_QUESTION, StepContacts } from './StepContacts'
import { STEP_ELD_QUESTION, StepEld } from './StepEld'
import { VaultLanding } from './VaultLanding'

const TOTAL_STEPS = 3

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The addresses as the backend will read them: split, trimmed, blanks dropped. */
function addressesIn(value: string): string[] {
  return value
    .split(',')
    .map((address) => address.trim())
    .filter(Boolean)
}

export interface VaultOnboardingDraft {
  contacts: ContactsDraft
  business: BusinessDraft
}

/** The landing state and the three steps behind it, all on the vault page. */
export function VaultOnboarding({
  orgId,
  onImportStart,
  onFinish,
}: {
  orgId: string
  onImportStart: () => void
  onFinish: () => void
}) {
  const { user } = useCurrentUser({ enabled: true })
  const confirm = useConfirmComplianceScope(orgId)
  const { draft: scopeDraft } = useComplianceScope(orgId)
  // null is the landing state; 1-3 are the steps.
  const [step, setStep] = useState<number | null>(null)
  const [draft, setDraft] = useState<VaultOnboardingDraft | null>(null)
  const [eldConnected, setEldConnected] = useState(false)
  // The signed-in user seeds the contacts once; what they type after that stands.
  const seeded: VaultOnboardingDraft = draft ?? {
    // No seed while the field is disabled: a number nobody can clear must not be saved.
    contacts: { email: user?.email ?? '', phone: '' },
    business: { hazmat: null, interstate: null },
  }

  // Confirming needs the fleet's USDOT; without one the steps would end in a 400.
  if (scopeDraft && !scopeDraft.hasDot) {
    return (
      <Banner variant="warning">
        Your fleet has no USDOT number yet. Add it under Fleet details and the vault will look the operation
        up.
      </Banner>
    )
  }

  if (step === null)
    return (
      <VaultLanding
        onStart={() => {
          onImportStart()
          setStep(1)
        }}
      />
    )

  const question =
    step === 1 ? STEP_CONTACTS_QUESTION : step === 2 ? STEP_BUSINESS_QUESTION : STEP_ELD_QUESTION

  const canAdvance =
    step === 1
      ? addressesIn(seeded.contacts.email).length > 0 &&
        addressesIn(seeded.contacts.email).every((address) => EMAIL.test(address))
      : step === 2
        ? seeded.business.hazmat !== null && seeded.business.interstate !== null
        : true

  /** Signing the scope off is what opens the vault; the ELD is asked for but never required. */
  const submit = () => {
    const phone = seeded.contacts.phone.trim()
    confirm.mutate(
      {
        interstate: seeded.business.interstate,
        hazmat: seeded.business.hazmat,
        notificationEmails: addressesIn(seeded.contacts.email),
        notificationPhones: phone ? [phone] : [],
      },
      {
        onSuccess: () => onFinish(),
        onError: (error) => toast.error(apiErrorDetail(error, 'Could not set up your vault.')),
      }
    )
  }

  return (
    <Card>
      <CardHeader>
        <span className="text-ink-500 font-mono text-xs">
          Step {step} of {TOTAL_STEPS}
        </span>
        <CardTitle>{question}</CardTitle>
      </CardHeader>
      <CardContent>
        {step === 1 && (
          <StepContacts value={seeded.contacts} onChange={(contacts) => setDraft({ ...seeded, contacts })} />
        )}
        {step === 2 && (
          <StepBusiness value={seeded.business} onChange={(business) => setDraft({ ...seeded, business })} />
        )}
        {step === 3 && <StepEld connected={eldConnected} onConnected={() => setEldConnected(true)} />}
      </CardContent>
      <CardFooter className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          disabled={confirm.isPending}
          onClick={() => setStep(step === 1 ? null : step - 1)}
        >
          Back
        </Button>
        <Button
          disabled={!canAdvance}
          loading={confirm.isPending ? 'Setting up…' : undefined}
          onClick={() => (step === TOTAL_STEPS ? submit() : setStep(step + 1))}
        >
          {step === TOTAL_STEPS ? 'Set up my vault' : 'Continue'}
        </Button>
      </CardFooter>
    </Card>
  )
}
